import { CheckCircle, Clock, Calendar, AlertTriangle, Package, FolderOpen, Zap, Component, Target, TrendingUp, Shield, Bug, Activity, Server, MapPin, Lock as LockIcon, Bell, AlertCircle, ShieldCheck, UserPlus, CreditCard } from 'lucide-react';
export const getRiskScoreComments = (category: string, score: number) => {
  if (category.toLowerCase() === 'operational_risk_score') {
    if (score >= 90) return 'Risk is critical. Significant maintainability and lifecycle issues threaten operational continuity. Immediate modernization is recommended.';
    if (score >= 70) return 'Risk is high. Outdated, deprecated, or infrequently maintained technologies increase operational risk. Prioritize upgrades and maintenance.';
    if (score >= 40) return 'Risk is moderate. Improve maintainability by addressing outdated, deprecated, or inactive technologies.';

    return 'Risk is low. Technologies are current, well-maintained, and have minimal concerns. Continue regular maintenance and monitoring.';
  }
  else if (category.toLowerCase() === 'total_technical_debt') {
    if (score >= 90) return 'Tech debt is critical. Immediate remediation is required to reduce risk and improve the overall health of the technology portfolio.';
    if (score >= 70) return 'Tech debt is high. Prioritize remediation to improve security, maintainability, and operational stability.';
    if (score >= 40) return 'Tech debt is moderate. Plan remediation to address identified issues before risks increase.';

    return 'Tech debt is low. Technology assets are well maintained with minimal risk. Continue regular monitoring and maintenance.';
  }
  else if (category.toLowerCase() === 'overall_risk_score') {
    if (score >= 90) return 'Risk is critical. Substantial risks require immediate remediation to reduce exposure and improve resilience.';
    if (score >= 70) return 'Risk is high. Significant security, compliance, and operational risks require prioritized mitigation.';
    if (score >= 40) return 'Risk is moderate. Review identified risks and plan remediation to reduce future impact.';

    return 'Risk is low. Technology landscape is healthy with minimal security, licensing, and operational risks. Continue regular monitoring and maintenance.';
  }

  else if (category.toLowerCase() === 'vulnerability_risk_score') {
    if (score >= 90) return 'Risk is critical. Severe security exposure requires immediate remediation to reduce potential impact.';
    if (score >= 70) return 'Risk is high. Significant vulnerability exposure requires prioritized remediation and stronger security controls.';
    if (score >= 40) return 'Risk is moderate. Review identified vulnerabilities and plan remediation to reduce exposure.';

    return 'Risk is low. Exposure to known vulnerabilities is minimal. Continue regular security monitoring.';
  }
  else if (category.toLowerCase() === 'license_risk_score') {
    if (score >= 90) return 'Risk is critical. Significant licensing or compliance concerns require immediate remediation to minimize legal risk and ensure compliance.';
    if (score >= 70) return 'Risk is high. Increased exposure to licensing or compliance issues. Prioritize license reviews and remediation.';
    if (score >= 40) return 'Risk is moderate. Review license obligations and address compliance gaps to reduce legal and operational risks.';

    return 'Risk is low. Licenses present minimal compliance concerns. Continue regular license reviews.';
  }

  return '';
}

export const getLicenseTyper = (category: string) => {
  switch (category?.toLowerCase()) {
    case 'cla':
      return 'CLA';
    case 'commercial':
      return 'Commercial';
    case 'copyleft':
      return 'Copyleft';
    case 'copyleft-limited':
      return 'Weak Copyleft';
    case 'free-restricted':
      return 'Free Restricted';
    case 'generic':
      return 'Generic';
    case 'include-in-notice-file':
      return 'Include in Notice File';
    case 'include-source-code-offer-in-notice-file':
      return 'Include Source Code Offer in Notice File';
    case 'patent-license':
      return 'Patent License';
    case 'permissive':
      return 'Permissive';
    case 'proprietary-free':
      return 'Proprietary Free';
    case 'public-domain':
      return 'Public Domain';
    case 'source-available':
      return 'Source Available';
    case 'unstated-license':
      return 'Unstated License';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

export const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-100/80 text-green-800 dark:bg-green-500/20 dark:text-green-300';
    case 'in progress':
      return 'bg-blue-100/80 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300';
    case 'planning':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    case 'not started':
      return 'bg-gray-100/80 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300';
    case 'failed':
      return 'bg-red-100/80 text-red-800 dark:bg-red-500/20 dark:text-red-300';
    case 'scheduled':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    case 'active':
      return 'bg-green-100/80 text-green-800 dark:bg-green-500/20 dark:text-green-300';
    case 'development':
      return 'bg-blue-100/80 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300';
    case 'maintenance':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    case 'archived':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    case 'inactive':
      return 'bg-gray-100/80 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300';
    case 'pending':
    case 'invited':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    case 'published':
      return 'bg-green-100/80 text-green-800 dark:bg-green-500/20 dark:text-green-300';
    case 'draft':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    default:
      return 'bg-gray-100/80 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300';
  }
};

export const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'in progress':
      return <Clock className="w-4 h-4 text-blue-600" />;
    case 'planning':
      return <Calendar className="w-4 h-4 text-yellow-600" />;
    case 'not started':
      return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    case 'failed':
      return <AlertTriangle className="w-4 h-4 text-red-600" />;
    case 'scheduled':
      return <Clock className="w-4 h-4 text-yellow-600" />;
    default:
      return <Clock className="w-4 h-4 text-gray-600" />;
  }
};

export const getRiskScoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

export const getDevelopmentPhaseColor = (phase: string) => {
  switch (phase.toLowerCase()) {
    case 'development':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30';
    case 'staging':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-500/20 dark:text-yellow-300 dark:border-yellow-500/30';
    case 'production':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/30';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30';
  }
};

export const getPriorityColor = (impact: string) => {
  switch (impact.toLowerCase()) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/30';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/30';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-500/20 dark:text-yellow-300 dark:border-yellow-500/30';
    case 'low':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/30';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30';
  }
};

export const getStatusBadge = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return <span className="bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300 px-3 py-1 rounded-full text-xs">Paid</span>;
    case 'pending':
      return <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300 px-3 py-1 rounded-full text-xs">Pending</span>;
    case 'failed':
      return <span className="bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300 px-3 py-1 rounded-full text-xs">Failed</span>;
    case 'completed':
      return <span className="bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300 px-3 py-1 rounded-full text-xs">Completed</span>;
    case 'in-progress':
      return <span className="bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300 px-3 py-1 rounded-full text-xs">In Progress</span>;
    default:
      return <span className="bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300 px-3 py-1 rounded-full text-xs">{status}</span>;
  }
};


export const getTypeColor = (type: string) => {
  switch (type) {
    case 'Security Report':
      return 'bg-red-100/80 text-red-800 dark:bg-red-500/20 dark:text-red-300';
    case 'Vulnerability Assessment':
      return 'bg-orange-100/80 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300';
    case 'Compliance Report':
      return 'bg-purple-100/80 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300';
    case 'Risk Assessment':
      return 'bg-blue-100/80 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300';
    default:
      return 'bg-gray-100/80 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300';
  }
};

export const getScanTypeColor = (type: string) => {
  switch (type) {
    case "Web Application":
      return "bg-purple-100/80 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300";
    case "Network":
      return "bg-blue-100/80 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300";
    case "Database":
      return "bg-orange-100/80 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300";
    case "API":
      return "bg-green-100/80 text-green-800 dark:bg-green-500/20 dark:text-green-300";
    case "Mobile":
      return "bg-pink-100/80 text-pink-800 dark:bg-pink-500/20 dark:text-pink-300";
    default:
      return "bg-gray-100/80 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300";
  }
};

export const getRoleColor = (role: string) => {
  switch (role) {
    case 'Security Lead':
      return 'bg-purple-100/80 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300';
    case 'Security Manager':
      return 'bg-blue-100/80 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300';
    case 'Penetration Tester':
      return 'bg-red-100/80 text-red-800 dark:bg-red-500/20 dark:text-red-300';
    case 'Security Analyst':
      return 'bg-orange-100/80 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300';
    case 'Junior Security Analyst':
      return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
    default:
      return 'bg-gray-100/80 text-gray-800 dark:bg-gray-500/20 dark:text-gray-300';
  }
};



export const getScoreClass = (score: number): string => {
  if (score >= 8.1) return 'text-red-600 font-semibold dark:text-red-400';
  if (score >= 6.1) return 'text-orange-600 font-semibold dark:text-orange-400';
  if (score >= 4.1) return 'text-yellow-600 font-semibold dark:text-yellow-400';
  return 'text-green-600 font-semibold dark:text-green-400';
};


export const getIconForTitle = (title: string) => {
  const titleLower = title.toLowerCase();

  if (titleLower.includes('application')) return Package;
  if (titleLower.includes('project')) return FolderOpen;
  if (titleLower.includes('scan')) return Zap;
  if (titleLower.includes('component')) return Component;
  if (titleLower.includes('technical') || titleLower.includes('debt')) return Target;
  if (titleLower.includes('business') || titleLower.includes('risk')) return TrendingUp;
  if (titleLower.includes('overall') || titleLower.includes('score')) return Shield;
  if (titleLower.includes('vulnerability')) return Bug;
  if (titleLower.includes('license')) return LockIcon;
  if (titleLower.includes('operational')) return Activity;
  if (titleLower.includes('encryption')) return Server;
  if (titleLower.includes('provenance')) return MapPin;
  return AlertTriangle;
};

export const parseGitHubUrl = (url: string): { mainUrl: string; version: string } => {
  // Default values
  const result = {
    mainUrl: url.trim(),
    version: ''
  };

  if (!url || typeof url !== 'string') {
    return result;
  }

  const trimmedUrl = url.trim();

  const treeMatch = trimmedUrl.match(/^(https?:\/\/github\.com\/[^\/]+\/[^\/]+)\/tree\/(.+)$/);

  if (treeMatch) {
    result.mainUrl = treeMatch[1];
    result.version = treeMatch[2];
    return result;
  }

  // Match GitHub URLs with commit pattern
  const commitMatch = trimmedUrl.match(/^(https?:\/\/github\.com\/[^\/]+\/[^\/]+)\/commit\/([a-f0-9]+)$/);

  if (commitMatch) {
    result.mainUrl = commitMatch[1];
    result.version = commitMatch[2];
    return result;
  }

  // If it's a clean GitHub repo URL without tree/commit, keep it as is
  const cleanRepoMatch = trimmedUrl.match(/^https?:\/\/github\.com\/[^\/]+\/[^\/]+\/?$/);

  if (cleanRepoMatch) {
    result.mainUrl = trimmedUrl.replace(/\/$/, ''); // Remove trailing slash
    result.version = '';
    return result;
  }

  // For any other case, return the original URL
  return result;
};

export const getColorForTitle = (title: string, index: number = 0) => {
  const colors = ['green', 'blue', 'yellow', 'red', 'purple', 'indigo', 'pink', 'orange'];

  if (title.toLowerCase().includes('application')) return 'blue';
  if (title.toLowerCase().includes('vulnerability')) return 'red';
  if (title.toLowerCase().includes('license')) return 'orange';
  if (title.toLowerCase().includes('operational')) return 'blue';
  if (title.toLowerCase().includes('technical')) return 'yellow';
  if (title.toLowerCase().includes('business')) return 'purple';
  if (title.toLowerCase().includes('overall')) return 'indigo';
  if (title.toLowerCase().includes('component')) return 'green';
  if (title.toLowerCase().includes('encryption')) return 'pink';

  return colors[index % colors.length];
};

export const getRiskLevelBadgeClass = (riskLevel: string | undefined | null) => {
  const value = (riskLevel || '').toLowerCase();
  if (value === 'critical') return 'bg-red-100/80 text-red-800 dark:bg-red-500/20 dark:text-red-300';
  if (value === 'high') return 'bg-orange-100/80 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300';
  if (value === 'medium') return 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';
  if (value === 'low') return 'bg-green-100/80 text-green-800 dark:bg-green-500/20 dark:text-green-300';
  return 'bg-muted text-muted-foreground';
};



export const getNotificationIcon = (type: string | null) => {
  switch (type) {
    case 'scan_completed':
    case 'scan_failed':
      return <ShieldCheck className="w-4 h-4" />;
    case 'team_invite':
    case 'member_joined':
    case 'team_member_joined':
    case 'workgroup_invite':
    case 'workgroup_join_request':
    case 'workgroup_request_accepted':
    case 'workgroup_request_rejected':
      return <UserPlus className="w-4 h-4" />;
    case 'billing_update':
    case 'payment_failed':
    case 'subscription_expired':
      return <CreditCard className="w-4 h-4" />;
    case 'system_alert':
      return <AlertCircle className="w-4 h-4" />;
    default:
      return <Bell className="w-4 h-4" />;
  }
};

export const getNotificationColor = (type: string | null) => {
  switch (type) {
    case 'scan_completed':
      return 'text-green-600 dark:text-green-400';
    case 'scan_failed':
    case 'payment_failed':
    case 'system_alert':
      return 'text-red-600 dark:text-red-400';
    case 'team_invite':
    case 'member_joined':
    case 'team_member_joined':
    case 'workgroup_invite':
    case 'workgroup_join_request':
    case 'workgroup_request_accepted':
    case 'workgroup_request_rejected':
      return 'text-blue-600 dark:text-blue-400';
    case 'billing_update':
      return 'text-orange-600 dark:text-orange-400';
    case 'subscription_expired':
      return 'text-yellow-600 dark:text-yellow-400';
    default:
      return 'text-muted-foreground';
  }
};



export const getNotificationTypeLabel = (type: string | null) => {
  switch (type) {
    case 'scan_completed':
      return 'Scan Completed';
    case 'scan_failed':
      return 'Scan Failed';
    case 'team_invite':
      return 'Team Invite';
    case 'member_joined':
      return 'Member Joined';
    case 'billing_update':
      return 'Billing Update';
    case 'payment_failed':
      return 'Payment Failed';
    case 'subscription_expired':
      return 'Subscription Expired';
    case 'system_alert':
      return 'System Alert';
    default:
      return 'General';
  }
};

export const formatMemberSinceDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    return dateString;
  }
};

export const formatBillingDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-GB', options);
  } catch (error) {
    return String(dateString);
  }
};

export const toNumber = (value: unknown): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatScore = (score: number): string => (Number.isFinite(score) ? score.toFixed(2) : '0.0');

export const isSubscriptionValid = (validUpto: string | null | undefined): boolean => {
  if (!validUpto) return false;
  const validDate = new Date(validUpto);
  const now = new Date();
  return validDate >= now;
};
